import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../App';
import {
  AlertTriangle,
  BookOpen,
  BrainCircuit,
  CheckCircle2,
  FileText,
  FlaskConical,
  KeyRound,
  Library,
  LockKeyhole,
  Power,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import './Assistant.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit } from '../lib/zeit';

const CONFIG_TOGGLES = [
  { key: 'llmEnabled', label: 'KI-Synthese', detail: 'Modell für Support-Antworten freischalten' },
  { key: 'ticketDraftEnabled', label: 'Ticket-Entwürfe', detail: 'Supportentwürfe vorbereiten' },
  { key: 'chatEnabled', label: 'Live-Chat', detail: 'Kunde-App freigeben' },
  { key: 'landingChatEnabled', label: 'Landing-Chat', detail: 'Öffentliche Website freigeben' },
];

const formatIndexedAt = value => datumUndZeit(value, { jahr: 'voll', leer: 'noch nicht gemeldet' });

function Metric({ icon: Icon, label, value, detail, tone = 'neutral' }) {
  return (
    <article className={`assistant-metric is-${tone}`}>
      <div className="assistant-metric-icon"><Icon size={19} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </article>
  );
}

export default function Assistant() {
  const [stats, setStats] = useState(null);
  const [reindexMsg, setReindexMsg] = useState('');
  const { can } = useAuth();
  const mayConfig = !!(can && can('settings.manage'));
  const [cfg2, setCfg2] = useState(null);
  const [modelInput, setModelInput] = useState('');
  const [testMsg, setTestMsg] = useState(null);

  const loadStats = useCallback(() => {
    api.get('/admin/assistant/stats').then(r => setStats(r.data)).catch(() => {});
    if (mayConfig) {
      api.get('/admin/assistant/config')
        .then(r => {
          setCfg2(r.data);
          setModelInput(r.data.model || '');
        })
        .catch(() => {});
    }
  }, [mayConfig]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const saveConfig = async patch => {
    try {
      const r = await api.post('/admin/assistant/config', patch);
      setCfg2(r.data);
      toast.success('Gespeichert');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen');
      return false;
    }
  };

  // ASSISTENT-MODELLWAHL-001: Es wird KEIN Schlüssel mehr gesendet — der liegt in
  // der Server-Umgebung. Gespeichert wird ausschließlich die Modellwahl.
  const saveModel = async () => {
    const model = modelInput.trim();
    if (!model || model === cfg2?.model) return;
    await saveConfig({ model });
  };

  const runTest = async () => {
    setTestMsg({ pending: true });
    try {
      const r = await api.post('/admin/assistant/test');
      // SUPPORT-ASSISTENT-LAEUFT-001: Es zählt, welches Modell GEANTWORTET hat — nicht,
      // welches angefragt wurde. Weicht die Antwort ab, oder ist die Wahl gar nicht
      // wirksam geworden (Rückfall auf die Voreinstellung), steht der Grund dahinter.
      const geantwortet = r.data.answeredModel || r.data.model;
      setTestMsg({
        ok: true,
        text: `${geantwortet}: ${r.data.sample}${r.data.note ? ` — ${r.data.note}` : ''}`,
      });
    } catch (e) {
      setTestMsg({ ok: false, text: e.response?.data?.error || 'Testlauf fehlgeschlagen' });
    }
  };

  const reindex = async () => {
    if (!window.confirm('FAQ-Regale neu einlesen? Die drei Regale (Kunde, Fahrer, Partner) werden neu aufgebaut.')) return;
    setReindexMsg('');
    try {
      const r = await api.post('/admin/assistant/reindex');
      const parts = [];
      const faq = r.data.faq;
      const faqDriver = r.data.faqDriver;
      const faqPartner = r.data.faqPartner;
      if (faq) parts.push(faq.chunks != null ? `Kunden-FAQ: ${faq.chunks} Einträge` : `Kunden-FAQ: ${faq.error}`);
      if (faqDriver) parts.push(faqDriver.chunks != null ? `Fahrer-FAQ: ${faqDriver.chunks} Einträge` : `Fahrer-FAQ: ${faqDriver.error}`);
      if (faqPartner) parts.push(faqPartner.chunks != null ? `Partner-FAQ: ${faqPartner.chunks} Einträge` : `Partner-FAQ: ${faqPartner.error}`);
      setReindexMsg(`Neu eingelesen — ${parts.join(' · ') || 'ok'}.`);
      loadStats();
    } catch (e) {
      setReindexMsg(e.response?.data?.error || 'Neu-Einlesen fehlgeschlagen.');
    }
  };

  const activeChannels = cfg2
    ? CONFIG_TOGGLES.filter(toggle => cfg2[toggle.key]).length
    : 0;

  return (
    <div className="assistant-page">
      <PageHeader
        title="Support-KI"
        description="Der Assistent für Kunde, Fahrer und Partner antwortet ausschließlich aus den drei freigegebenen FAQ-Regalen — nie aus interner Dokumentation."
        actions={
        <button type="button" className="assistant-reindex" onClick={reindex}>
          <RefreshCw size={15} />
          FAQ-Regale neu einlesen
        </button>
      }
      />

      <section className="assistant-boundary" aria-label="Wissensgrenze">
        <div className="assistant-boundary-icon"><Library size={23} /></div>
        <div>
          <span>Controlled knowledge boundary</span>
          <h2>Drei FAQ-Regale sind die einzige Wissensquelle</h2>
          <p>Kunden-, Fahrer- und Partner-Chat lesen jeweils nur ihr eigenes Regal. „Frag die Doku" ist abgebaut — der Dokumentationsordner wird nicht mehr eingelesen (Owner-Entscheid 16.08.2026).</p>
        </div>
        <div className="assistant-boundary-status">
          <span><i /> Quellengebunden</span>
          <small>FAQ · {formatIndexedAt(stats?.faqIndexedAt)}</small>
        </div>
      </section>

      {reindexMsg && (
        <div className="assistant-reindex-message" role="status">
          <CheckCircle2 size={16} />
          {reindexMsg}
        </div>
      )}

      {stats && (
        <section className="assistant-metrics" aria-label="Wissenskennzahlen">
          <Metric icon={BookOpen} label="Kunden-FAQ" value={stats.faq?.customer ?? 0} detail="Einträge im Regal" tone="purple" />
          <Metric icon={FileText} label="Fahrer-FAQ" value={stats.faq?.driver ?? 0} detail="Einträge im Regal" />
          <Metric icon={Library} label="Partner-FAQ" value={stats.faq?.partner ?? 0} detail="Einträge im Regal" />
          <Metric icon={Sparkles} label="KI-Synthese" value={stats.llm ? 'AN' : 'Aus'} detail={stats.llm ? 'Modell aktiv' : 'ruhend ohne Schlüssel'} tone={stats.llm ? 'green' : 'neutral'} />
        </section>
      )}

      {mayConfig && cfg2 && (
        <section className="assistant-config">
          <div className="assistant-section-heading">
            <div>
              <span>03 · Admin Control</span>
              <h2>KI-Aktivierung &amp; Kanäle</h2>
              <p>Nur für Admins mit <code>settings.manage</code>. Der Schlüssel liegt in der Server-Umgebung — hier wird kein Schlüssel eingegeben oder gespeichert.</p>
            </div>
            <div className="assistant-config-summary">
              <span>{activeChannels}/4 aktiv</span>
              <small>{cfg2.keyName || 'OPENAI_API_KEY'} {cfg2.hasKey ? 'gesetzt' : 'NICHT gesetzt'}</small>
            </div>
          </div>

          <div className="assistant-key-grid">
            <label>
              <span><KeyRound size={13} /> Anbieter &amp; Schlüssel</span>
              <input
                aria-label="Anbieter und Schlüsselquelle"
                value={`${cfg2.provider === 'anthropic' ? 'Anthropic' : 'OpenAI'} · Schlüssel ${cfg2.keyName || 'OPENAI_API_KEY'} ${cfg2.hasKey ? 'gesetzt' : 'NICHT gesetzt'} (Server-Umgebung)`}
                readOnly
                disabled
              />
            </label>
            <label>
              <span><BrainCircuit size={13} /> Laufendes Modell</span>
              <input
                aria-label="Laufendes Modell"
                value={`${cfg2.model}${cfg2.model === cfg2.defaultModel ? ' · Standard' : ''}`}
                readOnly
                disabled
              />
            </label>
          </div>

          <div className="assistant-model-choice">
            <div className="assistant-section-heading">
              <div>
                <span><Sparkles size={13} /> Modellwahl</span>
                <p>Wählbar sind nur Modelle mit geprüftem Preis-Snapshot (Eingabe / Ausgabe je Mio. Token). Die Wahl wirkt sofort auf die nächste Antwort.</p>
              </div>
            </div>
            <div className="assistant-toggle-grid">
              {(cfg2.models || []).map(entry => {
                const selected = (modelInput || cfg2.model) === entry.model;
                // ASSISTENT-LUNA-WAEHLBAR-001: Beide Sperrgründe gleich behandelt —
                // fehlender Serverschlüssel UND fehlender Umgebungspreis (Luna) stehen
                // sichtbar im Kleintext, statt das Modell zu verstecken.
                const priceAvailable = entry.price_available !== false;
                const priceText = priceAvailable
                  ? `${entry.input_usd_per_million.toFixed(2)} $ ein / ${entry.output_usd_per_million.toFixed(2)} $ aus je Mio. Token`
                  : `Preis nicht hinterlegt (${entry.price_name || 'Umgebungswert fehlt'})`;
                return (
                  <button
                    type="button"
                    key={entry.model}
                    className={`assistant-toggle ${selected ? 'is-on' : ''}`}
                    aria-pressed={selected}
                    data-model={entry.model}
                    disabled={!entry.key_available || !priceAvailable}
                    onClick={() => setModelInput(entry.model)}
                  >
                    <span className="assistant-toggle-icon"><BrainCircuit size={15} /></span>
                    <span>
                      <strong>{entry.label}</strong>
                      <small>
                        {priceText}
                        {!entry.key_available ? ` · ${entry.key_name} fehlt auf dem Server` : ''}
                      </small>
                    </span>
                    <i>{entry.active ? 'läuft' : entry.default ? 'Standard' : selected ? 'gewählt' : ''}</i>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="assistant-save-key"
              onClick={saveModel}
              disabled={!modelInput.trim() || modelInput.trim() === cfg2.model}
            >
              <LockKeyhole size={14} />
              Modell speichern
            </button>
          </div>

          <div className="assistant-toggle-grid">
            {CONFIG_TOGGLES.map(toggle => {
              const isOn = !!cfg2[toggle.key];
              return (
                <button
                  type="button"
                  className={`assistant-toggle ${isOn ? 'is-on' : ''}`}
                  data-config-key={toggle.key}
                  aria-pressed={isOn}
                  key={toggle.key}
                  onClick={() => saveConfig({ [toggle.key]: !isOn })}
                >
                  <span className="assistant-toggle-icon"><Power size={15} /></span>
                  <span>
                    <strong>{toggle.label}</strong>
                    <small>{toggle.detail}</small>
                  </span>
                  <i>{isOn ? 'AN' : 'Aus'}</i>
                </button>
              );
            })}
          </div>

          <div className="assistant-test-row">
            <div>
              <strong>Konfiguration verifizieren</strong>
              <span>Ein echter Testlauf prüft Schlüssel, Endpunkt und Modell erst nach deinem Klick.</span>
            </div>
            <button
              type="button"
              className="assistant-test-button"
              onClick={runTest}
              disabled={!cfg2.hasKey || !cfg2.llmEnabled}
            >
              <FlaskConical size={14} />
              Testlauf
            </button>
          </div>

          {testMsg && (
            <div className={`assistant-test-message ${testMsg.ok ? 'is-ok' : ''} ${testMsg.pending ? 'is-pending' : ''}`} role="status">
              {testMsg.pending
                ? <RefreshCw className="assistant-spin" size={15} />
                : testMsg.ok
                  ? <CheckCircle2 size={15} />
                  : <AlertTriangle size={15} />}
              {testMsg.pending ? 'Teste Verbindung…' : testMsg.text}
            </div>
          )}

          <footer className="assistant-config-boundary">
            <ShieldCheck size={17} />
            <p>Ticket-Entwürfe benötigen Schlüssel, aktive KI-Synthese und aktiven Ticket-Schalter. Quelle bleibt ausschließlich die freigegebene Kunden-FAQ.</p>
          </footer>
        </section>
      )}

    </div>
  );
}
